import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function Report() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    async function loadReport() {
      try {
        const res = await api.get(`/classify/search/${id}`);
        setReport(res.data);
      } catch (error) {
        toast.error('Failed to load report');
        navigate('/search');
      } finally {
        setLoading(false);
      }
    }
    loadReport();
  }, [id, navigate]);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/export/pdf/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `classify_report_${id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-gray-500">Loading report...</p>
        </div>
      </div>
    );
  }

  if (!report) return null;

  const cls = report.result?.classification || {};
  const ver = report.result?.verification || {};
  const op = report.result?.operational || {};
  const atm = report.result?.atmosphere || {};
  const acc = report.result?.accessibility || {};
  const park = report.result?.parking || {};
  const pay = report.result?.payment || {};
  const chain = report.result?.chain_info || {};
  const ic = report.result?.industry_codes || {};
  const ic2 = report.result?.secondary_industry_codes || null;
  const ic3 = report.result?.tertiary_industry_codes || null;
  const cc = report.result?.custom_codes || {};
  const uw = report.result?.uw_enrichment;
  const uscore = report.result?.understudy_score;
  const prop = report.result?.property_data;
  const img = report.result?.business_image;
  const liq = report.result?.liquor_license || {};

  const confColor = cls.confidence_level === 'high' ? 'confidence-high' : 
                    cls.confidence_level === 'medium' ? 'confidence-medium' : 'confidence-low';

  return (
    <div className="max-w-4xl mx-auto">
      {/* Top Actions Bar */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate('/search')} className="text-teal hover:text-teal-600 font-medium text-sm">
          ← New Search
        </button>
        <div className="flex gap-2">
          <button 
            onClick={handleExportPDF} 
            disabled={exporting}
            className="btn-outline text-sm py-2 px-4"
          >
            {exporting ? 'Exporting...' : '📄 Export PDF'}
          </button>
        </div>
      </div>

      {/* ============================================ */}
      {/* SECTION 1: Business Header */}
      {/* ============================================ */}
      <div className="card mb-6">
        <div className="flex gap-5">
          {img?.url && (
            <img 
              src={img.url} 
              alt="Business" 
              className="w-36 h-28 rounded-lg object-cover flex-shrink-0 bg-gray-100"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="flex-1">
            <h1 className="font-heading text-2xl text-navy mb-1">
              {ver.google_name || report.input?.business_name}
            </h1>
            <div className="flex items-center gap-1 text-gray-500 text-sm">
              <span>📍</span>
              {ver.google_maps_url ? (
                <a href={ver.google_maps_url} target="_blank" rel="noopener noreferrer" className="text-teal hover:underline">
                  {ver.google_address}
                </a>
              ) : (
                <span>{ver.google_address}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-400">
              {ver.phone && <span>📞 {ver.phone}</span>}
              {ver.website && (
                <a href={ver.website} target="_blank" rel="noopener noreferrer" className="text-teal hover:underline">
                  🌐 Website
                </a>
              )}
              <span>Status: {ver.business_status === 'OPERATIONAL' ? '✅ Active' : ver.business_status || 'Unknown'}</span>
              <span className="capitalize">LOB: {report.input?.lob}</span>
            </div>
            {ver.description && (
              <p className="text-sm text-gray-500 mt-2 italic">{ver.description}</p>
            )}
            <div className="text-xs text-gray-300 mt-2">
              Report generated {new Date(report.createdAt).toLocaleString()}
            </div>
          </div>

          {/* Understudy Risk Score Badge — always shown */}
          {uscore?.score != null && (
            <RiskBadge score={uscore.score} rag={uscore.rag} tierLabel={uscore.tier_label} 
                       topRisks={uscore.top_risk_factors} favorables={uscore.favorable_factors} />
          )}
        </div>
      </div>

      {/* Understudy Risk Summary */}
      {report.result?.risk_summary && uscore?.score != null && (
        <div className={`mb-6 rounded-xl overflow-hidden border ${
          uscore.score >= 90 ? 'bg-emerald-50 border-emerald-200' :
          uscore.score >= 75 ? 'bg-green-50 border-green-200' :
          uscore.score >= 45 ? 'bg-yellow-50 border-yellow-200' :
          uscore.score >= 25 ? 'bg-orange-50 border-orange-200' :
          'bg-red-50 border-red-200'
        }`}>
          <div className={`px-4 py-2.5 flex items-center gap-2 ${
            uscore.score >= 90 ? 'bg-emerald-600' :
            uscore.score >= 75 ? 'bg-green-600' :
            uscore.score >= 45 ? 'bg-yellow-500' :
            uscore.score >= 25 ? 'bg-orange-500' :
            'bg-red-600'
          }`}>
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-white text-sm font-semibold tracking-wide">Understudy Risk Summary</span>
          </div>
          <div className="px-5 py-4">
            <p className={`text-sm leading-relaxed ${
              uscore.score >= 75 ? 'text-green-800' :
              uscore.score >= 45 ? 'text-yellow-800' :
              uscore.score >= 25 ? 'text-orange-800' :
              'text-red-800'
            }`}>
              {report.result.risk_summary}
            </p>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* SECTION 2: Classification */}
      {/* ============================================ */}
      <div className="card mb-6">
        <h2 className="section-title">Classification</h2>
        
        {(() => {
          const hasPropCodes = cc?.has_custom_codes === true;
          
          const ragColor = (conf) => {
            if (conf >= 90) return 'text-green-700 bg-green-50 border-green-200';
            if (conf >= 70) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
            return 'text-red-700 bg-red-50 border-red-200';
          };
          const ragDot = (conf) => conf >= 90 ? '🟢' : conf >= 70 ? '🟡' : '🔴';

          if (hasPropCodes) {
            // ============================================
            // MODE A: Tenant has proprietary codes
            // Hero card with prop code stack + reference columns with RAG
            // ============================================
            const propTiers = [
              cc.primary ? { ...cc.primary, label: 'Primary' } : null,
              cc.secondary ? { ...cc.secondary, label: 'Alt 1' } : null,
              cc.tertiary ? { ...cc.tertiary, label: 'Alt 2' } : null,
            ].filter(Boolean);

            // Build industry code tiers for aggregation
            const codeTiers = [];
            if (cls.primary) codeTiers.push({ conf: cls.primary.confidence, codes: ic });
            if (cls.secondary) codeTiers.push({ conf: cls.secondary.confidence, codes: ic2 || {} });
            if (cls.tertiary) codeTiers.push({ conf: cls.tertiary.confidence, codes: ic3 || {} });

            const aggregate = (system) => {
              const map = {};
              codeTiers.forEach(t => {
                const code = t.codes?.[system] || 'N/A';
                const desc = t.codes?.[system + '_desc'] || '';
                if (code === 'N/A') return;
                if (!map[code]) map[code] = { code, desc, totalConf: 0 };
                map[code].totalConf += t.conf;
              });
              return Object.values(map).sort((a, b) => b.totalConf - a.totalConf);
            };

            const sicAgg = aggregate('sic');
            const naicsAgg = aggregate('naics');
            const aaisAgg = aggregate('aais');

            const CodeColumn = ({ title, items }) => (
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold uppercase text-gray-400 mb-2 text-center">{title}</div>
                {items.map((item, i) => (
                  <div key={i} className={`border rounded-lg p-2 mb-1 text-center ${i === 0 ? ragColor(item.totalConf) : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                    <div className="text-xs">{i === 0 ? ragDot(item.totalConf) : ''} {Math.round(item.totalConf)}%</div>
                    <div className={`font-bold ${i === 0 ? 'text-lg' : 'text-sm'}`}>{item.code}</div>
                    <div className="text-xs mt-0.5 truncate" title={item.desc}>{item.desc}</div>
                  </div>
                ))}
              </div>
            );

            return (
              <>
                {/* Hero: Proprietary Code Stack — all tiers in one card */}
                <div className={`border-2 rounded-lg p-5 mb-4 ${ragColor(propTiers[0]?.confidence || 0)}`}>
                  {propTiers.map((tier, i) => (
                    <div key={i} className={i === 0 ? 'text-center mb-3' : 'flex items-center justify-between px-4 py-2 border-t border-gray-200'}>
                      {i === 0 ? (
                        <>
                          <div className="text-xs font-bold uppercase mb-2">
                            {ragDot(tier.confidence)} Primary Classification
                          </div>
                          <div className="text-3xl font-bold">{tier.description}</div>
                          <div className="text-sm text-gray-500 mt-1">Code: {tier.code}</div>
                          <div className="text-sm font-semibold mt-1">{tier.confidence}% Confidence</div>
                        </>
                      ) : (
                        <>
                          <span className="text-xs font-bold uppercase text-gray-400 w-12">{tier.label}</span>
                          <span className="font-bold text-gray-600">{tier.code}</span>
                          <span className="text-sm text-gray-500 flex-1 text-center">{tier.description}</span>
                          <span className="text-sm text-gray-400">{tier.confidence}%</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {/* SIC / NAICS / AAIS with aggregation and RAG */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {sicAgg.length > 0 && <CodeColumn title="SIC (7-Digit)" items={sicAgg} />}
                  {naicsAgg.length > 0 && <CodeColumn title="NAICS" items={naicsAgg} />}
                  {aaisAgg.length > 0 && <CodeColumn title="AAIS" items={aaisAgg} />}
                </div>
              </>
            );
          } else {
            // ============================================
            // MODE B: No proprietary codes
            // 3 columns with primary + alternates and RAG
            // ============================================
            const tiers = [];
            if (cls.primary) tiers.push({ conf: cls.primary.confidence, codes: ic });
            if (cls.secondary) tiers.push({ conf: cls.secondary.confidence, codes: ic2 || {} });
            if (cls.tertiary) tiers.push({ conf: cls.tertiary.confidence, codes: ic3 || {} });

            // Confidence aggregation per code system
            const aggregate = (system) => {
              const map = {};
              tiers.forEach(t => {
                const code = t.codes?.[system] || 'N/A';
                const desc = t.codes?.[system + '_desc'] || '';
                if (code === 'N/A') return;
                if (!map[code]) map[code] = { code, desc, totalConf: 0 };
                map[code].totalConf += t.conf;
              });
              return Object.values(map).sort((a, b) => b.totalConf - a.totalConf);
            };

            const sicAgg = aggregate('sic');
            const naicsAgg = aggregate('naics');
            const aaisAgg = aggregate('aais');

            // Show the primary classification name as context
            const primaryName = cls.primary?.name || '';
            const primaryConf = cls.primary?.confidence || 0;

            const CodeColumn = ({ title, items }) => (
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold uppercase text-gray-400 mb-2 text-center">{title}</div>
                {items.map((item, i) => (
                  <div key={i} className={`border rounded-lg p-2 mb-1 text-center ${i === 0 ? ragColor(item.totalConf) : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                    <div className="text-xs">{i === 0 ? ragDot(item.totalConf) : ''} {Math.round(item.totalConf)}%</div>
                    <div className={`font-bold ${i === 0 ? 'text-lg' : 'text-sm'}`}>{item.code}</div>
                    <div className="text-xs mt-0.5 truncate" title={item.desc}>{item.desc}</div>
                  </div>
                ))}
              </div>
            );

            return (
              <>

                {/* 3-column grid with aggregation */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {sicAgg.length > 0 && <CodeColumn title="SIC (7-Digit)" items={sicAgg} />}
                  {naicsAgg.length > 0 && <CodeColumn title="NAICS" items={naicsAgg} />}
                  {aaisAgg.length > 0 && <CodeColumn title="AAIS" items={aaisAgg} />}
                </div>
              </>
            );
          }
        })()}

        {/* Chain Info — only show for franchise codes */}
        {chain?.is_chain && ['5000906', '5000707', '5000705'].includes(cls.primary?.code) && (
          <div className="bg-purple-50 rounded-lg p-3 mb-3 text-sm">
            <span className="font-semibold text-purple-700">🔗 Franchise Chain:</span>{' '}
            {chain.chain_name}
          </div>
        )}

        {/* Liquor License */}
        {liq.found && (
          <div className="bg-amber-50 rounded-lg p-3 mb-3 text-sm">
            <span className="font-semibold text-amber-700">🍺 Liquor License Verified</span>
            {liq.license_type && <span> — {liq.license_type}</span>}
            {liq.has_full_bar && <span className="ml-2 text-amber-600 font-bold">Full Bar</span>}
            {liq.matched_name && <div className="text-xs text-amber-600 mt-1">Licensed to: {liq.matched_name} ({liq.match_method})</div>}
          </div>
        )}

        {/* Rationale */}
        {cls.rationale && (
          <p className="text-sm text-gray-500 mt-2">📋 {cls.rationale}</p>
        )}
      </div>

      {/* ============================================ */}
      {/* SECTION 3: Operational Profile */}
      {/* ============================================ */}
      <div className="card mb-6">
        <h2 className="section-title">Operational Profile</h2>
        
        {/* Service Options */}
        <BooleanGrid title="Service Options" items={[
          { label: 'Dine-In', value: op.dine_in },
          { label: 'Takeout', value: op.takeout },
          { label: 'Delivery', value: op.delivery },
          { label: 'Curbside', value: op.curbside_pickup },
          { label: 'Reservable', value: op.reservable },
        ]} />

        {/* Food Service */}
        <BooleanGrid title="Food Service" items={[
          { label: 'Breakfast', value: op.serves_breakfast },
          { label: 'Lunch', value: op.serves_lunch },
          { label: 'Dinner', value: op.serves_dinner },
          { label: 'Brunch', value: op.serves_brunch },
          { label: 'Coffee', value: op.serves_coffee },
          { label: 'Dessert', value: op.serves_dessert },
          { label: 'Vegetarian', value: op.serves_vegetarian },
        ]} />

        {/* Alcohol */}
        <BooleanGrid title="Alcohol" items={[
          { label: 'Beer', value: op.serves_beer },
          { label: 'Wine', value: op.serves_wine },
          { label: 'Cocktails', value: op.serves_cocktails },
        ]} />

        {/* Ratings & Pricing */}
        <div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-4">
          {op.rating && <span>⭐ {op.rating}/5 ({op.review_count} reviews)</span>}
          {op.price_level && <span>💰 {op.price_level}</span>}
          {op.price_range_min != null && op.price_range_max != null && (
            <span>💵 ${op.price_range_min} – ${op.price_range_max}</span>
          )}
        </div>

        {/* Hours */}
        {op.hours?.descriptions && (
          <div className="mt-4 text-sm text-gray-500">
            <div className="font-semibold text-navy mb-1">
              Hours
              {op.hours.open_now != null && (
                <span className={`ml-2 text-xs font-bold ${op.hours.open_now ? 'text-green-600' : 'text-red-500'}`}>
                  {op.hours.open_now ? '● Open Now' : '● Closed'}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
              {op.hours.descriptions.map((h, i) => <div key={i}>{h}</div>)}
            </div>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* SECTION 4: Atmosphere */}
      {/* ============================================ */}
      <SectionIfAnyData data={atm}>
        <div className="card mb-6">
          <h2 className="section-title">Atmosphere</h2>
          <BooleanGrid items={[
            { label: 'Outdoor Seating', value: atm.outdoor_seating },
            { label: 'Live Music', value: atm.live_music },
            { label: 'Good for Children', value: atm.good_for_children },
            { label: 'Good for Groups', value: atm.good_for_groups },
            { label: 'Good for Sports', value: atm.good_for_watching_sports },
            { label: 'Kids Menu', value: atm.menu_for_children },
            { label: 'Dogs Allowed', value: atm.allows_dogs },
            { label: 'Has Restroom', value: atm.has_restroom },
          ]} />
        </div>
      </SectionIfAnyData>

      {/* ============================================ */}
      {/* SECTION 5: Accessibility, Parking & Payment */}
      {/* ============================================ */}
      <SectionIfAnyData data={{ ...acc, ...park, ...pay }}>
        <div className="card mb-6">
          <h2 className="section-title">Accessibility, Parking & Payment</h2>
          
          <SectionIfAnyData data={acc}>
            <BooleanGrid title="Wheelchair Accessibility" items={[
              { label: 'Parking', value: acc.wheelchair_parking },
              { label: 'Entrance', value: acc.wheelchair_entrance },
              { label: 'Restroom', value: acc.wheelchair_restroom },
              { label: 'Seating', value: acc.wheelchair_seating },
            ]} />
          </SectionIfAnyData>

          <SectionIfAnyData data={park}>
            <BooleanGrid title="Parking" items={[
              { label: 'Free Lot', value: park.free_parking_lot },
              { label: 'Paid Lot', value: park.paid_parking_lot },
              { label: 'Free Street', value: park.free_street_parking },
              { label: 'Paid Street', value: park.paid_street_parking },
              { label: 'Valet', value: park.valet_parking },
              { label: 'Free Garage', value: park.free_garage_parking },
              { label: 'Paid Garage', value: park.paid_garage_parking },
            ]} />
          </SectionIfAnyData>

          <SectionIfAnyData data={pay}>
            <BooleanGrid title="Payment" items={[
              { label: 'Credit Cards', value: pay.accepts_credit_cards },
              { label: 'Debit Cards', value: pay.accepts_debit_cards },
              { label: 'Cash Only', value: pay.accepts_cash_only },
              { label: 'NFC / Tap', value: pay.accepts_nfc },
            ]} />
          </SectionIfAnyData>
        </div>
      </SectionIfAnyData>

      {/* ============================================ */}
      {/* SECTION 6: Risk & Location Intelligence */}
      {/* ============================================ */}
      {uw && (
        <div className="card mb-6">
          <h2 className="section-title">Risk & Location Intelligence</h2>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Crime Data */}
            <div>
              <h3 className="font-semibold text-navy text-sm mb-2">Crime Data</h3>
              <div className="space-y-2">
                <DataRow label="Crime Score" value={uw.crime?.score} />
                <DataRow label="Violent Crime" value={uw.crime?.violent_crime_rate != null ? `${uw.crime.violent_crime_rate}%` : null} />
                <DataRow label="Property Crime" value={uw.crime?.property_crime_rate != null ? `${uw.crime.property_crime_rate}%` : null} />
                <DataRow label="Risk Level" value={uw.crime?.risk_level !== 'N/A' ? uw.crime?.risk_level : null} 
                  valueClass={uw.crime?.risk_level === 'Low' ? 'text-green-600' : uw.crime?.risk_level === 'High' ? 'text-red-600' : 'text-yellow-600'} />
              </div>
              {uw.calls_911?.volume_12mo != null && (
                <div className="mt-3">
                  <DataRow label="911 Calls (12mo)" value={uw.calls_911.volume_12mo} />
                </div>
              )}
            </div>

            {/* Sentiment */}
            <div>
              <h3 className="font-semibold text-navy text-sm mb-2">Sentiment Analysis</h3>
              <div className="space-y-2">
                <DataRow label="Google Rating" value={uw.sentiment?.google_rating ? `${uw.sentiment.google_rating}/5` : null} />
                <DataRow label="Reviews" value={uw.sentiment?.google_review_count || null} />
                <DataRow label="Summary" value={uw.sentiment?.summary !== 'N/A' ? uw.sentiment?.summary : null} />
              </div>
            </div>
          </div>

          {/* Risk Indicators — only show flagged ones */}
          {uw.risk_indicators && Object.values(uw.risk_indicators).some(v => v === true) && (
            <div className="mt-6">
              <h3 className="font-semibold text-navy text-sm mb-3">Risk Indicators</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(uw.risk_indicators)
                  .filter(([_, val]) => val === true)
                  .map(([key]) => (
                  <div 
                    key={key}
                    className="text-xs px-3 py-2 rounded-lg font-medium bg-red-50 text-red-700 border border-red-200"
                  >
                    ⚠️ {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nearby Businesses */}
          {uw.nearby && (
            <div className="mt-6">
              <h3 className="font-semibold text-navy text-sm mb-3">
                Nearby Businesses 
                <span className="font-normal text-gray-400 ml-1">
                  ({uw.nearby.total_neighbors || 0} within 150m)
                </span>
              </h3>
              {uw.nearby.sufficient_data ? (
                <div>
                  {/* Summary signal badge */}
                  {(() => {
                    const hasHigh = uw.nearby.high_negatives?.length > 0;
                    const hasMild = uw.nearby.mild_negatives?.length > 0;
                    const hasLateNight = uw.nearby.late_night_businesses?.length >= 2;
                    const posCount = uw.nearby.positives?.length || 0;
                    
                    if (hasHigh) return (
                      <div className="text-xs px-3 py-2 rounded-lg font-medium bg-red-50 text-red-700 border border-red-200 inline-block mb-3">
                        🚩 High-risk businesses in immediate vicinity
                      </div>
                    );
                    if (hasMild && hasLateNight) return (
                      <div className="text-xs px-3 py-2 rounded-lg font-medium bg-orange-50 text-orange-700 border border-orange-200 inline-block mb-3">
                        ⚠️ Late-night district with some negative indicators
                      </div>
                    );
                    if (hasMild) return (
                      <div className="text-xs px-3 py-2 rounded-lg font-medium bg-yellow-50 text-yellow-700 border border-yellow-200 inline-block mb-3">
                        ⚠️ Some negative businesses nearby
                      </div>
                    );
                    if (hasLateNight) return (
                      <div className="text-xs px-3 py-2 rounded-lg font-medium bg-orange-50 text-orange-700 border border-orange-200 inline-block mb-3">
                        🌙 Late-night district — {uw.nearby.late_night_businesses.length} bars/clubs nearby
                      </div>
                    );
                    if (posCount > 0) return (
                      <div className="text-xs px-3 py-2 rounded-lg font-medium bg-green-50 text-green-700 border border-green-200 inline-block mb-3">
                        ✓ Clean area — {posCount} professional/commercial neighbor{posCount > 1 ? 's' : ''}
                      </div>
                    );
                    return (
                      <div className="text-xs px-3 py-2 rounded-lg font-medium bg-gray-50 text-gray-500 border border-gray-200 inline-block mb-3">
                        Neutral area — no notable indicators
                      </div>
                    );
                  })()}

                  {/* Flagged businesses listed by name */}
                  {(uw.nearby.high_negatives?.length > 0 || uw.nearby.mild_negatives?.length > 0) && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {(uw.nearby.high_negatives || []).map((n, i) => (
                        <div key={`hn-${i}`} className="text-xs px-3 py-2 rounded-lg font-medium bg-red-50 text-red-700 border border-red-200">
                          🚩 {n.name}
                        </div>
                      ))}
                      {(uw.nearby.mild_negatives || []).map((n, i) => (
                        <div key={`mn-${i}`} className="text-xs px-3 py-2 rounded-lg font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                          ⚠️ {n.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-400">
                  {uw.nearby.summary || 'Insufficient nearby data for analysis'}
                </div>
              )}
            </div>
          )}

          {/* Website Scan */}
          {uw.website_scan && (
            <div className="mt-6">
              <h3 className="font-semibold text-navy text-sm mb-2">Website Scan</h3>
              {uw.website_scan.scan_successful ? (
                <div className="text-sm text-gray-600">
                  {uw.website_scan.title && <p><strong>Title:</strong> {uw.website_scan.title}</p>}
                  {uw.website_scan.description && <p className="mt-1"><strong>Description:</strong> {uw.website_scan.description}</p>}
                  {uw.website_scan.indicators_found?.length > 0 && (
                    <p className="mt-1 text-red-600">
                      <strong>Flags Found:</strong> {uw.website_scan.indicators_found.join(', ')}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-400">
                  {uw.website_scan.is_404 ? (
                    <span className="text-yellow-600">⚠️ {uw.website_scan.uw_note || 'Website not found'}</span>
                  ) : (
                    <span>Website scan unavailable</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* SECTION 7: Property Data (add-on) */}
      {/* ============================================ */}
      {prop?.available && prop.parsed && (
        <div className="card mb-6">
          <h2 className="section-title">Property Overview</h2>
          <p className="text-xs text-gray-400 mb-4">Source: HazardHub Property Risk API</p>

          {/* Property Specific Details — full width */}
          {prop.parsed.property_details && (
            <PropertyTable section={prop.parsed.property_details} />
          )}

          {/* Flood + Fire side by side */}
          {(prop.parsed.flood || prop.parsed.fire) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {prop.parsed.flood && <PropertyTable section={prop.parsed.flood} />}
              {prop.parsed.fire && <PropertyTable section={prop.parsed.fire} />}
            </div>
          )}

          {/* Weather Details — full width */}
          {prop.parsed.weather && (
            <div className="mt-6">
              <PropertyTable section={prop.parsed.weather} />
            </div>
          )}

          {/* Environmental — full width */}
          {prop.parsed.environmental && (
            <div className="mt-6">
              <PropertyTable section={prop.parsed.environmental} />
            </div>
          )}

          {/* Services + Crime side by side */}
          {(prop.parsed.services || prop.parsed.crime) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {prop.parsed.services && <PropertyTable section={prop.parsed.services} />}
              {prop.parsed.crime && <PropertyTable section={prop.parsed.crime} />}
            </div>
          )}

          {/* Liquor License nearby */}
          {prop.parsed.liquor_nearby && (
            <div className="mt-6">
              <PropertyTable section={prop.parsed.liquor_nearby} />
            </div>
          )}

          {/* Match info */}
          {prop.address_matched && (
            <div className="mt-4 text-xs text-gray-400 border-t pt-3">
              Matched address: {prop.address_matched}
              {prop.match_score != null && ` (confidence: ${(prop.match_score * 100).toFixed(0)}%)`}
            </div>
          )}
        </div>
      )}

      {/* Property data unavailable message if checkbox was selected but no data */}
      {prop && !prop.available && prop.reason && (
        <div className="card mb-6">
          <h2 className="section-title">Property Overview</h2>
          <div className="text-sm text-gray-400 py-4">
            ⚠️ {prop.reason}
          </div>
        </div>
      )}

      {/* Charges */}
      {report.charges && report.charges.total > 0 && (
        <div className="card mb-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Report Add-On Charges</span>
            <div className="text-right">
              {report.charges.property_data > 0 && (
                <div className="text-gray-600">Property Data: ${report.charges.property_data.toFixed(2)}</div>
              )}
              <div className="font-bold text-navy mt-1">Total: ${report.charges.total.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

/** Risk score badge in header */
function RiskBadge({ score, rag, tierLabel, topRisks = [], favorables = [] }) {
  const ragStyles = {
    green_deep: { bg: 'bg-emerald-100 border-emerald-300', text: 'text-emerald-700' },
    green:      { bg: 'bg-green-50 border-green-200', text: 'text-green-600' },
    yellow:     { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-600' },
    orange:     { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-600' },
    red:        { bg: 'bg-red-50 border-red-200', text: 'text-red-600' },
    gray:       { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-600' }
  };
  const s = ragStyles[rag] || ragStyles.gray;
  
  return (
    <div className={`flex-shrink-0 text-center p-4 rounded-lg border ${s.bg} min-w-[140px]`}>
      <div className={`text-3xl font-bold ${s.text}`}>{score}</div>
      <div className="text-xs font-medium text-gray-500 mt-0.5">Understudy Score</div>
      <div className={`text-xs font-bold mt-1 ${s.text}`}>{tierLabel}</div>
      
      {/* Top risk/favorable factors */}
      {(topRisks.length > 0 || favorables.length > 0) && (
        <div className="mt-2 text-left text-[10px] leading-tight space-y-0.5 border-t border-gray-200 pt-2">
          {topRisks.slice(0, 3).map((f, i) => (
            <div key={i} className="text-red-600">⚠ {f.label}</div>
          ))}
          {favorables.slice(0, 2).map((f, i) => (
            <div key={i} className="text-green-600">✓ {f.label}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/** 
 * Only renders children if at least one value in data is non-null 
 */
function SectionIfAnyData({ data, children }) {
  if (!data) return null;
  const hasData = Object.values(data).some(v => v !== null && v !== undefined);
  if (!hasData) return null;
  return <>{children}</>;
}

/** 
 * Grid of boolean badges — only renders items with non-null values 
 */
function BooleanGrid({ title, items }) {
  const filtered = items.filter(i => i.value !== null && i.value !== undefined);
  if (filtered.length === 0) return null;

  return (
    <div className="mb-4">
      {title && <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{title}</div>}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {filtered.map((item, i) => (
          <div key={i} className={`text-center py-2 px-2 rounded-lg text-xs font-medium ${
            item.value === true ? 'bg-green-50 text-green-700 border border-green-200' :
            'bg-gray-50 text-gray-400 border border-gray-100'
          }`}>
            <div className="text-[10px] text-gray-400 mb-0.5">{item.label}</div>
            <div>{item.value === true ? '✓ Yes' : '✗ No'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Single data row — hides if value is null */
function DataRow({ label, value, valueClass = '' }) {
  if (value === null || value === undefined) return null;
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className={`font-medium ${valueClass || 'text-navy'}`}>{value}</span>
    </div>
  );
}

function formatPropertyValue(value, format) {
  if (value === null || value === undefined || value === 'N/A') return null;
  if (format === 'currency') return `$${Number(value).toLocaleString()}`;
  if (format === 'number') return Number(value).toLocaleString();
  if (format === 'score') return `${value}/100`;
  return String(value);
}

/** 
 * Property sub-section table with header and rows 
 * Renders like the PDF report: section header bar + label/value rows
 * Skips null values automatically
 */
function PropertyTable({ section }) {
  if (!section?.fields) return null;
  
  const rows = Object.entries(section.fields)
    .filter(([, f]) => f.value !== null && f.value !== undefined);
  
  if (rows.length === 0) return null;

  return (
    <div>
      <div className="bg-navy text-white text-sm font-semibold px-3 py-2 rounded-t-lg">
        {section.label}
      </div>
      <div className="border border-t-0 border-gray-200 rounded-b-lg divide-y divide-gray-100">
        {rows.map(([key, field]) => (
          <div key={key} className="flex justify-between px-3 py-2 text-sm hover:bg-gray-50">
            <span className="text-gray-500">{field.label}</span>
            <span className="font-medium text-navy text-right max-w-[60%]">
              <PropertyValue value={field.value} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Renders property values with letter grade badges when applicable
 * Values like "A – Very Low" or "D – High" get colored badges
 */
function PropertyValue({ value }) {
  if (value === null || value === undefined) return null;
  const str = String(value);

  // Check for letter grade pattern: starts with A-F, optionally followed by " – " or " - "
  const gradeMatch = str.match(/^([A-F])\s*[–\-]\s*(.+)$/);
  if (gradeMatch) {
    const grade = gradeMatch[1];
    const text = gradeMatch[2].trim();
    const colors = {
      A: 'bg-green-100 text-green-700 border-green-300',
      B: 'bg-blue-100 text-blue-700 border-blue-300',
      C: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      D: 'bg-orange-100 text-orange-700 border-orange-300',
      E: 'bg-red-100 text-red-700 border-red-300',
      F: 'bg-red-100 text-red-700 border-red-300',
    };
    const c = colors[grade] || 'bg-gray-100 text-gray-700 border-gray-300';
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold border ${c}`}>{grade}</span>
        <span>{text}</span>
      </span>
    );
  }

  // Stand-alone letter grade (just "A", "B", etc.)
  if (/^[A-F]$/.test(str.trim())) {
    const grade = str.trim();
    const colors = {
      A: 'bg-green-100 text-green-700 border-green-300',
      B: 'bg-blue-100 text-blue-700 border-blue-300',
      C: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      D: 'bg-orange-100 text-orange-700 border-orange-300',
      F: 'bg-red-100 text-red-700 border-red-300',
    };
    const c = colors[grade] || 'bg-gray-100 text-gray-700 border-gray-300';
    return <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold border ${c}`}>{grade}</span>;
  }

  return <span>{str}</span>;
}
